import { splitEncoding } from '@contentful/live-preview';
import classNames from 'classnames';

import GenericComponent, {
  type EnhancedSectionContent,
  type SectionContent,
} from '@/components/GenericComponent/GenericComponent';
import type { Contentful } from '@/contentful/contentful.types';
import useEcommMarket from '@/hooks/geo';
import {
  type GetConstrainedContent,
  useGetConstrainedContent,
} from '@/hooks/useGetConstrainedContent';
import type { BreakpointName } from '@/styles/viewportSize';
import type { SectionConstraintsCF } from '@/types';
import type { ReplaceFields } from '@/types/utility-types';
import { sanitizeList } from '@/utils/constraint';
import useStyles from './Section.styles';

export interface SectionType extends Contentful {
  fields: {
    content?: SectionContent[];
    showBottomDividerLine?: boolean;
    backgroundColor:
      | 'LightBlueFractal'
      | 'OffWhiteFractal'
      | 'OffWhite'
      | 'White'
      | 'LightBlue';
    showInMarkets?: 'All Markets' | 'Ecomm Only' | 'Non Ecomm Only';
    abTestingRenderInvisible?: boolean;
    id?: string;
    title?: string;
    isFeatured?: boolean;
    screenSizesToHide?: BreakpointName[];
  };
}

export type EnhancedSectionType = ReplaceFields<
  SectionType,
  Omit<SectionType['fields'], 'content'> & {
    content?: EnhancedSectionContent[];
  }
>;

interface Props {
  section: EnhancedSectionType;
  sectionPosition?: number;
}

const getStateConstrainedSections = (
  content: EnhancedSectionContent[] | undefined,
  getConstrainedContent: GetConstrainedContent
): EnhancedSectionContent[] => {
  const result: EnhancedSectionContent[] = [];
  content?.forEach((content: EnhancedSectionContent) => {
    if (content.sys.contentType.sys.id === 'sectionConstraints') {
      const stateConstrainedContent =
        getConstrainedContent<EnhancedSectionContent>({
          constrainedSection: content as SectionConstraintsCF,
        });
      if (stateConstrainedContent) result.push(...stateConstrainedContent);
      return;
    }

    result.push(content);
  });

  return result;
};

export function Section({ section, sectionPosition }: Props) {
  const {
    content,
    showBottomDividerLine = false,
    backgroundColor,
    showInMarkets = 'All Markets',
    abTestingRenderInvisible = false,
    id,
    isFeatured,
    screenSizesToHide,
  } = section?.fields || {};

  const { classes } = useStyles({
    screenSizesToHide: sanitizeList(screenSizesToHide) as BreakpointName[],
  });

  const { isInEcommMarket } = useEcommMarket();
  const getConstrainedContent = useGetConstrainedContent();

  if (
    (isInEcommMarket && showInMarkets === 'Non Ecomm Only') ||
    (!isInEcommMarket && showInMarkets === 'Ecomm Only')
  )
    return null;

  const stateConstrainedSections = getStateConstrainedSections(
    content,
    getConstrainedContent
  );

  return (
    <section
      id={splitEncoding(id || '').cleaned}
      className={classNames([
        showBottomDividerLine && classes.grayLine,
        abTestingRenderInvisible && classes.hide,
        classes[backgroundColor],
        screenSizesToHide && classes.hideOnScreenSizes,
      ])}
    >
      {stateConstrainedSections?.map((content, index) => (
        <GenericComponent
          key={content.sys.id}
          content={content}
          isFeatured={isFeatured}
          sectionPosition={sectionPosition}
          componentPosition={index}
        />
      ))}
    </section>
  );
}
